import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { Button, DataGrid, Modal } from "../../common";
import { selectedOrgIdAtom, toastOptionsAtom, userAtom } from "../../../atoms";
import {
  useCreateTruckMutation,
  useFetchOrganizationsQuery,
  useFetchTrucksQuery,
  useUpdateTruckMutation,
} from "../../../api";
import { useFetchAllUsersQuery } from "../../../api/userService";
import { OrganizationModel, TruckModel } from "../../../models";
import {
  TRUCK_ALL_FILTER,
  TRUCK_CAPACITY_UNIT,
  TRUCK_STATUS_CHIP_COLORS,
  TRUCK_STATUS_FILTERS,
  TRUCK_STATUS_LABELS,
} from "../../../constants";
import { Role } from "../../../enums";
import { DropdownItem, Truck, TruckFormData } from "../../../types";
import { getApiErrorMessage, theme } from "../../../utils";
import { CrewFilterChips } from "../../users/crew/fragments";
import { TruckForm, TruckSummaryCards } from "./fragments";

const EmptyCell = () => (
  <Typography sx={{ fontSize: 13, color: "var(--dashboard-text-secondary)" }}>
    —
  </Typography>
);

export const Trucks = () => {
  const [user] = useAtom(userAtom);
  const [queryParams] = useSearchParams();
  const [selectedOrgId, setSelectedOrgId] = useAtom(selectedOrgIdAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [statusFilter, setStatusFilter] = useState(TRUCK_ALL_FILTER);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [selectedTruck, setSelectedTruck] = useState<Truck>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);
  const isSuperAdmin = user?.roleCode === Role.SuperAdmin;
  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled: isSuperAdmin,
  });
  const fetchTrucksQuery = useFetchTrucksQuery(selectedOrgId, {
    enabled: selectedOrgId ? true : false,
  });
  const trucks = useMemo(
    () => fetchTrucksQuery.data ?? [],
    [fetchTrucksQuery.data],
  );
  const fetchAllUsersQuery = useFetchAllUsersQuery(selectedOrgId, {
    enabled: selectedOrgId ? true : false,
  });
  const drivers = useMemo(
    () => TruckModel.buildDriverDropdown(fetchAllUsersQuery.data ?? []),
    [fetchAllUsersQuery.data],
  );
  const createTruckMutation = useCreateTruckMutation();
  const updateTruckMutation = useUpdateTruckMutation();
  const isSaving =
    createTruckMutation.isLoading || updateTruckMutation.isLoading;

  useEffect(() => {
    if (fetchTrucksQuery.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [fetchTrucksQuery.isError]);

  useEffect(() => {
    if (createTruckMutation.isSuccess) {
      setIsFormOpen(false);
      fetchTrucksQuery.refetch();
      setToastOptions({
        open: true,
        message: "Truck created successfully",
        severity: "success",
      });
    }

    if (createTruckMutation.isError) {
      setToastOptions({
        open: true,
        message: getApiErrorMessage(createTruckMutation.error),
        severity: "error",
      });
    }
  }, [
    createTruckMutation.isSuccess,
    createTruckMutation.data,
    createTruckMutation.isError,
    createTruckMutation.error,
  ]);

  useEffect(() => {
    if (updateTruckMutation.isSuccess) {
      setIsFormOpen(false);
      fetchTrucksQuery.refetch();
      setToastOptions({
        open: true,
        message: "Truck updated successfully",
        severity: "success",
      });
    }

    if (updateTruckMutation.isError) {
      if (
        updateTruckMutation.error.response?.status === HttpStatusCode.NotFound
      ) {
        setIsFormOpen(false);
        fetchTrucksQuery.refetch();
      }

      setToastOptions({
        open: true,
        message: getApiErrorMessage(updateTruckMutation.error),
        severity: "error",
      });
    }
  }, [
    updateTruckMutation.isSuccess,
    updateTruckMutation.data,
    updateTruckMutation.isError,
    updateTruckMutation.error,
  ]);

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      setOrganizationsDropdown(
        OrganizationModel.buildOrganizationDropdown(
          fetchOrganizationsQuery.data,
        ),
      );
    } else if (fetchOrganizationsQuery.isError) {
      setOrganizationsDropdown([]);
    }
  }, [
    fetchOrganizationsQuery.isSuccess,
    fetchOrganizationsQuery.isError,
    fetchOrganizationsQuery.data,
  ]);

  useEffect(() => {
    if (organizationsDropdown.length > 0 && !selectedOrgId) {
      setSelectedOrgId(
        Number(queryParams.get("orgId")) ||
          user?.orgId ||
          (organizationsDropdown[0].value as number),
      );
    }
  }, [organizationsDropdown, selectedOrgId, setSelectedOrgId]);

  const filteredTrucks = useMemo(
    () =>
      trucks.filter(
        (truck) =>
          statusFilter === TRUCK_ALL_FILTER || truck.status === statusFilter,
      ),
    [trucks, statusFilter],
  );

  const handleFilterChange = (setFilter: (value: string) => void) => {
    return (value: string) => {
      setFilter(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };
  };

  const handleOpenForm = (truck?: Truck) => {
    setSelectedTruck(truck);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  const handleSaveTruck = (data: TruckFormData) => {
    if (selectedTruck) {
      updateTruckMutation.mutate({
        orgId: selectedOrgId,
        id: selectedTruck.id,
        ...TruckModel.buildTruckPayload(data, selectedTruck),
      });
      return;
    }

    createTruckMutation.mutate({
      orgId: selectedOrgId,
      ...TruckModel.buildTruckPayload(data),
    });
  };

  const columns: GridColDef<Truck>[] = [
    {
      field: "truckCode",
      headerName: "Truck ID",
      width: 120,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "var(--dashboard-text-primary)",
          }}
        >
          {row.truckCode}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "Truck Name",
      flex: 1.2,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--dashboard-text-primary)",
            }}
          >
            {row.name}
          </Typography>
          {row.licensePlate && (
            <Typography
              noWrap
              sx={{ fontSize: 11, color: "var(--dashboard-text-secondary)" }}
            >
              {row.licensePlate}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "capacity",
      headerName: "Capacity",
      width: 120,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: 13 }}>
          {row.capacity} {TRUCK_CAPACITY_UNIT}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => TRUCK_STATUS_LABELS[row.status],
      renderCell: ({ row }) => (
        <Chip
          label={TRUCK_STATUS_LABELS[row.status]}
          size="small"
          variant="outlined"
          color={TRUCK_STATUS_CHIP_COLORS[row.status]}
          sx={{ borderRadius: "6px", fontWeight: 500 }}
        />
      ),
    },
    {
      field: "driverName",
      headerName: "Driver",
      width: 150,
      renderCell: ({ row }) =>
        row.driverName ? (
          <Typography sx={{ fontSize: 13 }}>{row.driverName}</Typography>
        ) : (
          <EmptyCell />
        ),
    },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Edit">
          <IconButton
            aria-label="edit"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenForm(row);
            }}
          >
            <EditOutlinedIcon
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box className="dashboard-page">
      <Box className="dashboard-header">
        <Box sx={{ minWidth: 0 }}>
          <Typography className="dashboard-title">Trucks</Typography>
          <Typography className="dashboard-subtitle">
            Fleet inventory and maintenance status
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {isSuperAdmin && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                maxWidth: { xs: "100%", sm: 320 },
                backgroundColor: "var(--dashboard-surface)",
                borderRadius: "var(--dashboard-radius-control)",
              }}
            >
              <InputLabel>Organization</InputLabel>
              <Select
                value={organizationsDropdown.length ? selectedOrgId : ""}
                label="Organization"
                onChange={(event) =>
                  setSelectedOrgId(Number(event.target.value))
                }
                sx={{
                  height: 42,
                  borderRadius: "var(--dashboard-radius-control)",
                }}
              >
                {organizationsDropdown.map((organization) => (
                  <MenuItem key={organization.value} value={organization.value}>
                    {organization.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            label="Add Truck"
            variantType="primary"
            icon={AddIcon}
            onClick={() => handleOpenForm()}
            sx={{
              height: 42,
              px: 2.5,
              whiteSpace: "nowrap",
              width: { xs: "100%", sm: "auto" },
            }}
          />
        </Box>
      </Box>

      <TruckSummaryCards trucks={trucks} />

      <Box className="dashboard-card" sx={{ mt: 3, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 2,
            p: 2,
            borderBottom: "1px solid var(--dashboard-border-row)",
          }}
        >
          <CrewFilterChips
            label="Status"
            options={TRUCK_STATUS_FILTERS}
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
          />
        </Box>

        <DataGrid
          columns={columns}
          rows={filteredTrucks}
          loading={
            fetchTrucksQuery.isLoading ||
            fetchTrucksQuery.isRefetching ||
            isSaving
          }
          checkboxSelection={false}
          autoHeight
          rowHeight={64}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          onRowClick={({ row }) => handleOpenForm(row)}
          sx={{
            border: "none",
            px: 2,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "var(--dashboard-text-secondary)",
            },
          }}
        />
      </Box>

      <Modal
        open={isFormOpen}
        onClose={handleCloseForm}
        title={selectedTruck ? selectedTruck.truckCode : "Add New Truck"}
        content={
          <TruckForm
            id="truckForm"
            defaultValues={selectedTruck}
            handleEdit={handleSaveTruck}
            drivers={drivers}
          />
        }
        form="truckForm"
        primaryButtonText={selectedTruck ? "Save Changes" : "Add Truck"}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleCloseForm}
        disablePrimaryButton={isSaving}
        sx={{
          modal: {
            width: { xs: "90%", sm: "70%", md: "55%", lg: "40%" },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          content: {
            overflowY: "auto",
            flex: 1,
          },
        }}
      />
    </Box>
  );
};
